import { useSelector } from "react-redux";

import { FilterSpecification, Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { SUBLAYERS } from "@/constants/map";
import { RootState } from "@/store";
import { LegendFilterType } from "@/store/slice/mapSlice";
import { SpatialAggregationType } from "@/types/common";
import { Dataset, DatasetData } from "@/types/dataset";
import { LayerType, SelectedFeature } from "@/types/map";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import {
  filterPointFeatures,
  generateNonPointMapFilter,
  isFeatureMatchingFilters,
  LayerSublayers
} from "@/utils/map";

type useLegendFilterProps = {
  map: Map;
  handleAddPointsLayer: (
    dataset: Dataset,
    pointsData: FeatureCollection<Geometry>,
    customId?: string,
    filteredData?: FeatureCollection<Geometry>,
    beforeId?: string
  ) => void;
  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void;
  handleClearSelectedState: (currentSelectedFeature: SelectedFeature) => void;
};

export const useLegendFilter = ({
  map,
  handleAddPointsLayer,
  handleRemoveSourceLayer,
  handleClearSelectedState
}: useLegendFilterProps) => {
  const { selectedFeature } = useSelector((state: RootState) => state.map);

  const handleFilterLayerByLegend = (
    newfilterLegends: LegendFilterType[],
    dataset: Dataset,
    rawDataset: DatasetData,
    beforeId?: string
  ) => {
    if (!map) return;
    const filters = newfilterLegends ?? [];
    const spatialType = dataset.properties.spatialType;
    const layers: LayerSublayers = SUBLAYERS(dataset.data.id)[spatialType];

    // Get attribute name - use stroke attribute for line datasets
    const field =
      spatialType === LayerType.LINE
        ? (dataset.style.stroke?.attribute_name ??
          dataset.selectedCategory.attribute_name)
        : dataset.selectedCategory.attribute_name;

    // Get data type - look up from categories for line datasets
    const dataType =
      spatialType === LayerType.LINE
        ? (dataset.categories.find((c) => c.attribute_name === field)
            ?.data_type ?? dataset.selectedCategory.data_type)
        : dataset.selectedCategory.data_type;

    const colorRanges = dataset.style.colorRanges;

    const nonNullRanges = colorRanges.filter(
      (r) => r.value !== "" && r.label?.toLowerCase() !== "null"
    );

    const top9Values = nonNullRanges
      .slice(0, -1)
      .map((r) => r.value)
      .filter((v): v is string => typeof v === "string");

    if (spatialType === LayerType.POINT) {
      handleRemoveSourceLayer(dataset.data.id, "poi");

      // Find column indices
      const gridIndex = dataset.categories.findIndex(
        (attr) => attr.attribute_name === GRID_ID_ATTRIBUTE
      );
      const catchmentIndex = dataset.categories.findIndex(
        (attr) => attr.attribute_name === CATCHMENT_ID_ATTRIBUTE
      );

      const attributeSchemas =
        gridIndex === -1 && catchmentIndex === -1
          ? dataset.categories
          : undefined;

      const originalFeatureCollection =
        convertDatasetResponseToFeatureCollection(rawDataset, attributeSchemas);

      let filteredFeatureCollection: FeatureCollection<Geometry> | undefined =
        undefined;

      if (filters.length > 0) {
        const filteredFeatures = filterPointFeatures(
          originalFeatureCollection.features,
          field,
          filters,
          dataType,
          top9Values
        );

        filteredFeatureCollection = {
          ...originalFeatureCollection,
          features: filteredFeatures
        };
      }

      handleAddPointsLayer(
        dataset,
        originalFeatureCollection,
        undefined,
        filteredFeatureCollection,
        beforeId ? beforeId : undefined
      );
    } else {
      // For non-point layers, apply filter to fill and outline layers
      const filterMap = generateNonPointMapFilter(
        field,
        filters,
        dataType,
        top9Values
      ) as FilterSpecification;

      if (layers.fill) map.setFilter(layers.fill, filterMap);
      if (layers.outline) map.setFilter(layers.outline, filterMap);
      if (layers.stroke) map.setFilter(layers.stroke, filterMap);
    }

    if (selectedFeature) {
      const value = selectedFeature.properties?.[field];
      if (
        isFeatureMatchingFilters(value, filters, dataType, top9Values) &&
        selectedFeature.source === dataset.data.id
      ) {
        handleClearSelectedState(selectedFeature);
      }
    }
  };

  return {
    handleFilterLayerByLegend
  };
};

export default useLegendFilter;
